import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import DefaultMap from '@/pages/notification/bio/DefaultMap.tsx'
import { format } from 'date-fns'
import ContactBadge from '@/component/badge/ContactBadge.tsx'
import { equalString, isEmpty } from '@/util/strings.ts'
import Input from '@/component/input/Input.tsx'
import Select, { ISelectOption } from '@/component/input/Select.tsx'
import BorderButton from '@/component/button/BorderButton.tsx'
import SolidButton from '@/component/button/SolidButton.tsx'
import useQueryGetUserDetail from '@/api/auth/getUser.ts'
import useQueryGetMe from '@/api/auth/getMe.ts'
import useQueryGetAlertEmergencyDetail from '@/api/notification/getAlertEmergencyDetail.ts'
import useQueryGetFallDetectionDetail from '@/api/notification/getFallDetectionDetail.ts'
import { EmergencyLiveAlertContext } from '@/pages/notification/emergency/EmergencyAlertModal.tsx'
import { EmergencyAlertConfirmStatus } from '@/api/notification/getNotificationSos.ts'
import {
    apiPatchEmergencyAlertAssignPerson,
    apiPatchEmergencyAlertProcess
} from '@/api/notification/patchEmergencyAlert.ts'
import {
    apiPatchFallDetectionAlertAssignPerson,
    apiPatchFallDetectionAlertProcess
} from '@/api/notification/patchFallDetectionAlert.ts'
import { useTranslation } from 'react-i18next'
import { EmergencyAlertType } from '@/api/notification/getLiveEmergencyAlert.ts'
import { useMapLocationStore } from '@/store/MapLocationStore.ts'
import { useNavigate } from 'react-router-dom'
import LocationIcon from '@/assets/ic_alert_location.svg?react'
import SpinnerIcon from '@/assets/ic_loading.svg?react'
import { useAudioStore } from '@/store/AudioStore.ts'

export interface IProps {
    className?: string
}

const EmergencyAlertDetail: React.FC<IProps> = ({ className }) => {
    const { t } = useTranslation()
    const { stop: stopAudio } = useAudioStore()

    const { id, refetchList, refetchAndResetSelection, activeTab } = useContext(EmergencyLiveAlertContext)
    const { data: emergencyData, refetch: refetchEmergencyDetail, isLoading: isEmergencyLoading } = useQueryGetAlertEmergencyDetail(
        activeTab === EmergencyAlertType.SOS ? id : undefined
    )
    const { data: fallData, refetch: refetchFallDetail, isLoading: isFallLoading } = useQueryGetFallDetectionDetail(
        activeTab === EmergencyAlertType.FALL ? id : undefined
    )

    const data = activeTab === EmergencyAlertType.SOS ? emergencyData : fallData
    const isLoading = activeTab === EmergencyAlertType.SOS ? isEmergencyLoading : isFallLoading
    const refetchDetail = activeTab === EmergencyAlertType.SOS ? refetchEmergencyDetail : refetchFallDetail

    const { data: user } = useQueryGetUserDetail(data?.protectee?.id)
    const { data: me } = useQueryGetMe()
    const [isExpandedMap, setExpandedMap] = useState(false)
    const navigate = useNavigate()
    const { setTargetLocation } = useMapLocationStore()

    // 특정 이메일 계정인지 확인
    const isMaskingRequired = useMemo(() => {
        return me?.email === 'dbd_cm@clomental.com'
    }, [me?.email])
    
    // 마스킹 처리 함수
    const getMaskedName = (name?: string) => {
        if (!isMaskingRequired || !name) return name
        if (name.length <= 1) return name
        return name.charAt(0) + '**'
    }
    
    const getMaskedContact = (contact?: string) => {
        if (!isMaskingRequired || !contact) return contact
        
        // 모든 전화번호 앞 3자리만 보여주고 나머지는 * 처리
        const contactParts = contact.split('-')
        if (contactParts.length === 3) {
            return `${contactParts[0]}-****-****`
        }
        
        // 기타 패턴의 전화번호 처리
        if (contact.length > 3) {
            return contact.substring(0, 3) + contact.substring(3).replace(/./g, '*')
        }
        
        return contact
    }
    
    const getMaskedAddress = (address?: string) => {
        if (!isMaskingRequired || !address) return address
        return '****'
    }

    const actionTypes: ISelectOption[] = useMemo(
        () => [
            {
                id: '기타',
                text: t('notification.sosModal.type.etc'),
                value: '기타'
            },
            {
                id: '119출동',
                text: t('notification.sosModal.type.119'),
                value: '119출동'
            },
            {
                id: '오작동',
                text: t('notification.sosModal.type.malfunction'),
                value: '오작동'

            }
        ],
        []
    )

    const [manager, setManager] = useState('')
    const [actionDetail, setActionDetail] = useState('')
    const [actionTypeId, setActionTypeId] = useState('기타')

    const onClickAssign = async () => {
        if (!id) return
        stopAudio()

        const { code } = activeTab === EmergencyAlertType.SOS
            ? await apiPatchEmergencyAlertAssignPerson(id, { manager_name: manager })
            : await apiPatchFallDetectionAlertAssignPerson(id, { manager_name: manager })

        if (code == 200) {
            await refetchDetail()
            refetchList()
        }
    }

    const getGenderName = useCallback((value: string) => {
        switch (value) {
            case '남':
                return t('notification.sosModal.gender.male')
            case '여':
                return t('notification.sosModal.gender.female')
            default:
                return value
        }
    }, [t])

    const onClickProcessAction = async () => {
        if (!id) return
        if (!actionTypeId) return
        stopAudio()

        const { code } = activeTab === EmergencyAlertType.SOS
            ? await apiPatchEmergencyAlertProcess(id, {
                task_result: actionTypeId,
                task_content: actionDetail
            })
            : await apiPatchFallDetectionAlertProcess(id, {
                task_result: actionTypeId,
                task_content: actionDetail
            })

        if (code == 200) {
            await refetchDetail()
            refetchAndResetSelection()
        }
    }

    const onClickLocationButton = useCallback(() => {
        const latitude = data?.alert?.location?.latitude
        const longitude = data?.alert?.location?.longitude
        
        if (
            typeof latitude === 'number' && 
            typeof longitude === 'number' && 
            !isNaN(latitude) && 
            !isNaN(longitude) &&
            isFinite(latitude) && 
            isFinite(longitude)
        ) {
            setTargetLocation({
                latitude: latitude,
                longitude: longitude
            })
            navigate('/dashboard')
        } else {
            console.warn('Invalid location coordinates:', { latitude, longitude })
        }
    }, [data?.alert?.location, setTargetLocation, navigate])

    useEffect(() => {
        setActionTypeId(data?.alert?.result || '기타')
        setActionDetail(data?.alert?.content || '')
        setManager(me?.name || '')
    }, [data, me])

    const canEditContent = useMemo(() => {
        const confirmStatus = data?.alert?.confirm

        switch (confirmStatus) {
            case EmergencyAlertConfirmStatus.DONE:
                return true
            case EmergencyAlertConfirmStatus.ASSIGN:
                return !!data?.alert?.isMyTask
            case  EmergencyAlertConfirmStatus.WAIT:
            default:
                return false
        }
    }, [data])


    return <div className={`${className}`}>
        {isLoading ? (
            <div className="flex justify-center items-center h-full">
                <SpinnerIcon className="animate-spin w-8 h-8" />
            </div>
        ) : (
            <div className={'flex flex-col border border-solid border-transparent border-l-gray2 py-[20px] px-[5px]'}>
                <div className="grid grid-cols-2 mb-5 bg-[#F0F5FF] rounded">
                    <div className="flex flex-col items-center py-2 px-4 border-r border-[#E5EDFF]">
                        <span className="text-xs font-medium text-[#3B82F6] mb-1">{t('notification.sos.table.emergency.column')}</span>
                        <span className="text-sm">
                            {data?.alert?.createdAt ? format(data.alert.createdAt, 'yyyy/MM/dd HH:mm') : '-'}
                        </span>
                    </div>
                    <div className="flex flex-col items-center py-2 px-4">
                        <span className="text-xs font-medium text-[#3B82F6] mb-1">{t('notification.sos.table.confirmed.column')}</span>
                        <span className="text-sm">
                            {data?.alert?.checkedAt ? format(data.alert.checkedAt, 'yyyy/MM/dd HH:mm') : '-'}
                        </span>
                    </div>
                </div>
                <div className={'grid grid-cols-2 gap-[10px]'}>
                    <div
                        className={'flex flex-col justify-center gap-[10px] p-[12px]'}>
                        <div
                            className={'bg-sub_b px-[15px] py-[8px] border border-solid border-main_b rounded-[5px] flex justify-between text-[20px] font-medium text-main_b'}>
                            <span>{getMaskedName(data?.protectee?.name)}</span>
                            <span>{getMaskedContact(data?.protectee?.contact)}</span>
                        </div>

                        <div
                            className={'relative flex flex-col bg-gray1 px-[15px] py-[8px] border border-solid border-gray2 rounded-[5px] gap-[14px]'}>
                            <div className={'flex justify-between items-center'}>
                                <span 
                                    className={'cursor-pointer flex-1'}
                                    onClick={() => setExpandedMap(!isExpandedMap)}>
                                    {getMaskedAddress(data?.alert?.location?.address)}
                                </span>
                                <div className={'flex items-center gap-2'}>
                                    {data?.alert?.location?.latitude && data?.alert?.location?.longitude && (
                                        <button
                                            className={'p-2 bg-main_b text-white rounded hover:bg-blue-600 transition-colors flex items-center gap-1 text-sm'}
                                            onClick={onClickLocationButton}
                                            title="대시보드에서 위치 보기">
                                            <LocationIcon className={'w-4 h-4'} />
                                            {t('dashboard.notification.location')}
                                        </button>
                                    )}
                                    <img 
                                        src="/ic_map_marker.png" 
                                        className={'cursor-pointer'}
                                        onClick={() => setExpandedMap(!isExpandedMap)}
                                    />
                                </div>
                            </div>
                            {isExpandedMap && data?.alert?.location && <DefaultMap
                                className={'min-h-[188px]'}
                                latitude={data?.alert?.location?.latitude}
                                longitude={data?.alert?.location?.longitude} />}
                        </div>
                        <p>{t('notification.sosModal.address')} : {getMaskedAddress(user?.address) || t('common.empty')}</p>
                        <p>{getGenderName(data?.protectee?.gender || '')} / {data?.protectee?.birthDate && format(data?.protectee?.birthDate, 'yy-MM-dd')}</p>

                        <p className={'text-left text-[14px] font-bold'}>{t('notification.sosModal.contact.title')}</p>
                        <div className={'grid grid-cols-2 gap-[10px]'}>
                            <div className={'flex gap-[10px]'}>
                                <ContactBadge>{t('notification.sosModal.contact.me')}</ContactBadge>
                                <p>{getMaskedContact(data?.protectee?.contact)}</p>
                            </div>
                            <div className={'flex gap-[10px]'}>
                                <ContactBadge>{t('notification.sosModal.contact.protector')}</ContactBadge>
                                <p>{getMaskedContact(data?.protector?.contact)}</p>
                            </div>
                            <div className={'flex gap-[10px]'}>
                                <ContactBadge>{t('notification.sosModal.contact.acquaintance')}</ContactBadge>
                                <p>{getMaskedContact(data?.protector?.secondaryContact)}</p>
                            </div>
                            <div className={'flex gap-[10px]'}>
                                <ContactBadge>{t('notification.sosModal.contact.admin')}</ContactBadge>
                                <p>{getMaskedContact(data?.protector?.tertiaryContact)}</p>
                            </div>
                        </div>

                        <p className={'text-[14px] font-bold mt-[10px]'}>{t('notification.sosModal.memo.title')}</p>
                        <div className={'p-[12px] bg-sub_b min-h-[158px]'}>
                            {isMaskingRequired ? '***************************' : data?.protectee?.memo}
                        </div>
                    </div>
                    <div className={'flex flex-col p-[12px] gap-[10px]'}>
                        {!equalString(data?.alert?.confirm, '미처리') &&
                            <div className={'flex gap-[13px] items-center'}>
                                <p className={'flex-none'}>{t('notification.sosModal.manager.title')}</p>
                                <Input disabled className={'w-full'} value={getMaskedName(data?.alert?.managerName ?? undefined) || ''} />
                            </div>}
                        <div className={'flex gap-[13px] items-center'}>
                            <p className={'flex-none'}>{t('notification.sosModal.type.title')}</p>
                            <Select className={'w-full'}
                                    value={actionTypes?.find(item => item.id == actionTypeId)}
                                    options={actionTypes}
                                    disabled={!canEditContent}
                                    onChanged={option =>
                                        setActionTypeId(option?.id)
                                    } />
                        </div>
                        <div
                            className={`flex flex-col p-[12px] border border-solid border-gray2 h-full text-[14px] ${!canEditContent && 'bg-[#f1f1f1]'}`}>
                            <p>{t('notification.sosModal.process.title')} : </p>
                            <textarea className={'border-none w-full h-full resize-none'}
                                      disabled={!canEditContent}
                                      value={actionDetail}
                                      onChange={e => setActionDetail(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className={'mt-[15.5px]'}>
                    {/* 하단 버튼들 */}
                    {data?.alert?.confirm == EmergencyAlertConfirmStatus.WAIT && <>
                        <div className={'flex gap-[20px] justify-center'}>
                            <input
                                className={'min-w-[370px] py-[13.5px] px-[15px] border border-solid border-sub_r'}
                                placeholder={t('notification.sosModal.manager.input.placeholder')}
                                value={manager || ''}
                                onChange={e => {
                                    const text = e.target.value
                                    setManager(text)
                                }} />
                            <BorderButton text={t('notification.sosModal.manager.btn.title')}
                                          className={'text-sub_r border-sub_r min-w-[200px]'}
                                          onClick={onClickAssign} />
                        </div>
                    </>}
                    {data?.alert?.confirm == EmergencyAlertConfirmStatus.ASSIGN && data?.alert?.isMyTask && <>
                        <div className={'flex gap-[20px] justify-center'}>
                            <SolidButton text={t('notification.sosModal.submit.confirm')} className={'min-w-[200px]'}
                                         disabled={isEmpty(actionDetail) || isEmpty(actionTypeId) || !canEditContent}
                                         onClick={onClickProcessAction} />
                        </div>
                    </>}
                    {data?.alert?.confirm == EmergencyAlertConfirmStatus.ASSIGN && !data?.alert?.isMyTask &&
                        <div className={'flex gap-[20px] justify-center'}>
                            <button className={'min-w-[200px] bg-sub_r min-h-[50px] text-[16px] px-[20px] text-white'}
                                    disabled>{t('notification.sosModal.manager.assign')}
                            </button>
                        </div>}
                    {data?.alert?.confirm == EmergencyAlertConfirmStatus.DONE && <>
                        <div className={'flex gap-[8px] justify-center'}>
                            <SolidButton text={t('notification.sosModal.submit.save')} className={'min-w-[200px]'}
                                         onClick={onClickProcessAction} />
                        </div>
                    </>}
                </div>
            </div>
        )}
    </div>
}

export default EmergencyAlertDetail